// @flow

import React, { type Node, useCallback } from 'react';
import styled from 'styled-components';

import { useOpenedIncomingPayment, getSafeCheckoutPaymentOptions } from '@tectonics/prs/incoming-payment';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useUniqueId } from '@tectonics/hooks';


import { getSupportedTraits } from '@tectonics/prs/feature';
import lookAndFeel from '@tectonics/look-and-feel/volcanes';
import { getEWalletCheckoutPaymentOptions } from '@tectonics/prs/incoming-payment/checkout-payment-options';
import { getSafeSupportedFeatureByName } from '@tectonics/prs/support';

import mercadoPagoIconSrc from './icons/mercado-pago-icon.svg';
import picpayIconSrc from './icons/picpay-icon.svg';
import ameIconSrc from './icons/ame-icon.svg';
import paypalIconSrc from './icons/paypal-icon.svg';
import machpaylIconSrc from './icons/machpay-icon.svg';
import mpesaIconSrc from './icons/mpesa-icon.svg';
import ussdIconSrc from './icons/ussd-icon.svg';
import { RadioButton } from '../../../../../../../commons/atoms';
import { intersect } from '@tectonics/commons/arrays';
import { Separator } from '../../../../../../../commons/atoms/separator';
import { ErrorLabel } from '../../../../../../../commons/molecules/error-label';

export function EWalletList(): Node {
  const incomingPayment = useOpenedIncomingPayment();
  let checkoutPaymentOptions = getSafeCheckoutPaymentOptions(incomingPayment);
  let ewalletAllowedBrands;

  ewalletAllowedBrands = getEWalletCheckoutPaymentOptions(checkoutPaymentOptions);

  const supportedDisplayEWalletBrandFeature = getSafeSupportedFeatureByName(
    'display_ewallet_accepts_brand',
    lookAndFeel.prsSupport
  );

  const supportedBrands = getSupportedTraits(supportedDisplayEWalletBrandFeature);
  const brandsToDisplay = intersect(supportedBrands, ewalletAllowedBrands);

  const [selectedEWallet, setSelectedEWallet] = useUserInputValue('selectedEWallet');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('selectedEWallet');
  const submitting = useSubmitting();

  const inputId = useUniqueId();
  const onChange = useCallback(e => setSelectedEWallet(e.target.value), [setSelectedEWallet]);

  if(selectedEWallet === '' && brandsToDisplay.length === 1) {
    setSelectedEWallet(brandsToDisplay[0]);
  }

  return (
    <div data-test-name="payment-type-code">
      {brandsToDisplay.map(brand => (
        <div key={brand}>
          <Label htmlFor={`${inputId}-${brand}`}>
            <RadioButton
              id={`${inputId}-${brand}`}
              name="payment_type_code"
              value={brand}
              checked={brand === selectedEWallet}
              onChange={onChange}
              disabled={submitting}
              data-error={formHasBeenSent && validationError ? 'error' : null}
              data-test-value={brand}
            />

            <Separator direction="h" size={4} />
            <EWalletIcon src={getEWalletIconSrc(brand)} alt={brand} />
            <Separator direction="h" size={4} />

            <span>{getEWalletName(brand)}</span>
          </Label>
        </div>
      ))}

      {formHasBeenSent && validationError && <ValidationMessage validationError={validationError} />}
    </div>
  );
}

const Label = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
`;

const EWalletIcon = styled.img``;

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return <ErrorLabel>Selecione o seu banco</ErrorLabel>;

    case 'server-error':
      return <ErrorLabel>{serverErrorMessage(validationError.payload)}</ErrorLabel>;

    default:
      return <ErrorLabel>Erro na seleção do banco</ErrorLabel>;
  }
}

function getEWalletName(ewallet: string): string {
  switch(ewallet) {
    case 'mercadopago':
      return 'Mercado Pago';

    case 'picpay':
      return 'PicPay';

    case 'ame':
      return 'Ame Digital';

    case 'paypal':
      return 'PayPal';

    case 'machpay':
      return 'MACH';

    case 'mpesa':
      return 'M-PESA';

    case 'ussd':
      return 'USSD';

    default:
      throw new Error(`EWalletList - ewallet not found ${ewallet}`);
  }
}

function getEWalletIconSrc(ewallet: string): string {
  switch(ewallet) {
    case 'mercadopago':
      return mercadoPagoIconSrc;

    case 'picpay':
      return picpayIconSrc;

    case 'ame':
      return ameIconSrc;

    case 'paypal':
      return paypalIconSrc;

    case 'machpay':
      return machpaylIconSrc;

    case 'mpesa':
      return mpesaIconSrc;

    case 'ussd':
      return ussdIconSrc;

    default:
      throw new Error(`EWalletList - Image source not found for ${ewallet}`);
  }
}
